// auth.js — the control center's auth core. Framework-free; React binds via
// AuthProvider. Ported from mono-layout-3's src/auth.js, which is the tested
// implementation of the backend's token contract, with two deliberate changes:
//
//  1. **No soft gate.** The main app has a pre-AUTH_REQUIRED-flip open mode and
//     all the machinery that goes with it (`authRequiredKnown`, learned hints,
//     "discover auth via a 401"). None of that applies here: every /admin route
//     is superuser-only and always enforced, so this app is signed-out or it is
//     nothing. Sign-in is the first screen, always.
//  2. **A superuser check on top of a valid session.** The backend rejects
//     non-superusers on every /admin route, but a provisioned operator would
//     otherwise sign in successfully and meet a wall of 403s with no
//     explanation. We read `user.is_superuser` and say so plainly.
//
// The token rules are the backend's, unchanged:
//   access token  → MEMORY ONLY, 15-minute TTL
//   refresh token → localStorage, OVERWRITTEN IMMEDIATELY on every rotation
//                   (the old one is dead server-side; replaying it revokes the
//                   whole family — see backend features/auth/tokens.py)
//   401 invalid_token → single-flight refresh → retry ONCE, never loop

import { API_BASE, GOOGLE_CLIENT_ID } from './config';

export { GOOGLE_CLIENT_ID };

// Distinct from the main app's 'mono.auth.refresh'. They are different origins
// so they cannot collide today, but a shared host tomorrow must not sign one
// app out by rotating the other's token.
const REFRESH_KEY = 'mono.cc.refresh';
const EXPIRY_MARGIN_S = 30;   // refresh this many seconds before nominal expiry

let accessToken = null;
let accessExpiresAt = 0;      // ms epoch
let currentUser = null;
let lastError = null;         // { code, message } — why we are signed out
let refreshInflight = null;   // single-flight promise
let initialized = false;
const listeners = new Set();

/* ── state + subscription ─────────────────────────────────────────── */

export function isSuperuser(user) {
  return !!user && user.is_superuser === true;
}

export function getAuthState() {
  return {
    user: currentUser,
    signedIn: !!currentUser,
    authorized: isSuperuser(currentUser),
    error: lastError,
  };
}

/**
 * Whether a stored session is about to be resumed — answerable SYNCHRONOUSLY,
 * before any effect runs, so the shell's first paint is the splash rather than
 * the sign-in screen followed by a flash of the app.
 */
export function sessionResumable() {
  return Boolean(refreshInflight) || (!!getRefreshToken() && !currentUser);
}

export function onAuthChange(cb) { listeners.add(cb); return () => listeners.delete(cb); }
function emit() { const s = getAuthState(); listeners.forEach((cb) => cb(s)); }

/* ── storage ──────────────────────────────────────────────────────── */

function getRefreshToken() {
  try { return localStorage.getItem(REFRESH_KEY); } catch { return null; }
}
function setRefreshToken(t) {
  try {
    if (t == null) localStorage.removeItem(REFRESH_KEY);
    else localStorage.setItem(REFRESH_KEY, t);
  } catch { /* private mode */ }
}

export function saveTokens(payload) {
  accessToken = payload.access_token || null;
  accessExpiresAt = Date.now() + Math.max(0, (payload.expires_in || 0) - EXPIRY_MARGIN_S) * 1000;
  if (payload.refresh_token) setRefreshToken(payload.refresh_token);   // overwrite immediately
  // /auth/google and /auth/refresh return the same bundle from one shared
  // backend helper, so a resume recovers the user without a /auth/me round
  // trip, and a superuser flag change propagates within one access TTL.
  if (payload.user) currentUser = payload.user;
  lastError = null;
  emit();
}

export function clearAuth(error = null) {
  accessToken = null; accessExpiresAt = 0; currentUser = null;
  lastError = error;
  setRefreshToken(null);
  emit();
}

/* ── lifecycle ────────────────────────────────────────────────────── */

/** Call once at app start. Registers multi-tab sync and resumes a stored
 *  session. Returns true when a resume was started. Idempotent (StrictMode). */
export function initAuth() {
  if (initialized) return false;
  initialized = true;

  window.addEventListener('storage', (e) => {
    if (e.key !== REFRESH_KEY) return;
    if (e.newValue === null) {
      accessToken = null; accessExpiresAt = 0; currentUser = null;
      emit();                                   // another tab signed out
    } else if (e.oldValue === null && e.newValue && !currentUser) {
      refreshTokens().catch(() => { /* cleared + emitted inside */ });
    }
    // A rotated value needs no action: refresh reads storage at call time.
  });

  if (!sessionResumable()) return false;
  if (refreshInflight) return true;
  // Emit when the boot resume SETTLES, whatever the outcome — a transient
  // failure deliberately keeps the refresh token (see refreshTokens), so
  // without this the splash would never come down.
  refreshTokens()
    .catch(() => { /* handled inside */ })
    .finally(() => { emit(); });
  return true;
}

/* ── refresh (single-flight, rotating) ────────────────────────────── */

export async function refreshTokens() {
  if (refreshInflight) return refreshInflight;    // queue behind one flight
  refreshInflight = (async () => {
    const rt = getRefreshToken();                 // read AT CALL TIME
    if (!rt) {
      const err = new Error('no_refresh_token'); err.code = 'no_refresh_token';
      throw err;
    }
    const resp = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: rt }),
    });
    if (!resp.ok) {
      let body = null; try { body = await resp.json(); } catch { /* non-JSON */ }
      const code = body?.detail?.code || null;
      const err = new Error(code || `refresh_failed_${resp.status}`);
      err.code = code; err.status = resp.status; err.body = body;
      // ONLY 401/403 (invalid_refresh · reuse_detected · inactive) mean the
      // session is gone. Anything else — 404, 5xx, offline, a tunnel hiccup —
      // is TRANSIENT: keep the token so the session survives a bad network.
      if (resp.status === 401 || resp.status === 403) {
        clearAuth({ code, message: body?.detail?.message || null });
      }
      throw err;
    }
    const payload = await resp.json();
    saveTokens(payload);                          // rotates refresh immediately
    return payload;
  })();
  try { return await refreshInflight; }
  finally { refreshInflight = null; }
}

/** A valid access token, refreshing first if expired/absent and possible. */
export async function ensureFreshAccess() {
  if (accessToken && Date.now() < accessExpiresAt) return accessToken;
  if (getRefreshToken()) { try { await refreshTokens(); } catch { /* handled */ } }
  return accessToken;
}

/* ── the request layer ────────────────────────────────────────────── */

export async function authFetch(url, options = {}) {
  const doFetch = async () => {
    const headers = new Headers(options.headers || {});
    const tok = await ensureFreshAccess();
    if (tok) headers.set('Authorization', `Bearer ${tok}`);
    return fetch(url, { ...options, headers });
  };

  let resp = await doFetch();
  if (resp.status === 401) {
    let code = null;
    try { code = (await resp.clone().json())?.detail?.code; } catch { /* non-JSON */ }
    if (code === 'invalid_token') {
      if (getRefreshToken()) {
        try {
          await refreshTokens();
          resp = await doFetch();                 // retry ONCE, never loop
        } catch { /* cleared inside refreshTokens */ }
      } else {
        clearAuth({ code: 'session_expired', message: 'Sign in again to continue.' });
      }
    }
  }
  return resp;
}

/* ── sign-in / sign-out ───────────────────────────────────────────── */

export async function signInWithGoogleCredential(idToken) {
  const resp = await fetch(`${API_BASE}/auth/google`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id_token: idToken }),
  });
  let body = null; try { body = await resp.json(); } catch { /* non-JSON */ }
  if (!resp.ok) {
    const err = new Error(body?.detail?.code || `auth_failed_${resp.status}`);
    err.code = body?.detail?.code || null; err.status = resp.status; err.body = body;
    throw err;
  }
  saveTokens(body);
  // A valid session that is not a superuser is a dead end here: every screen
  // in this app is /admin. Say so at the door rather than after five 403s.
  if (!isSuperuser(body.user)) {
    const err = new Error('not_superuser'); err.code = 'not_superuser';
    clearAuth({ code: 'not_superuser', message: body.user?.email || null });
    throw err;
  }
  return body.user;
}

/** Sign out of this browser only. */
export function signOutLocal() { clearAuth(); }

/** Revoke the whole session family server-side, then clear locally. */
export async function signOutEverywhere() {
  try { await authFetch(`${API_BASE}/auth/logout-all`, { method: 'POST' }); } catch { /* best effort */ }
  clearAuth();
}

/* ── util ─────────────────────────────────────────────────────────── */

/** Decode a JWT payload for DISPLAY only (the email in a not_provisioned
 *  message). No verification — the backend verifies. */
export function decodeJwtPayload(jwt) {
  try {
    const b64 = jwt.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(b64));
  } catch { return null; }
}
